import {calculateErrorMessage} from 'common/helpers'
import {PlayCircle} from 'lucide-react'
import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'

import {AssistantStep, stepsAreNavigable} from '../../modes/assistant/assistantResponseContract'
import {dispatchWalkthrough} from '../../walkthrough/utils/walkthroughDispatcher'

type AssistantStepsViewProps = {
  steps: AssistantStep[]
}

/**
 * The walkthrough widget of a `presentation: 'steps'` turn, rendered from `data.steps`.
 *
 * The steps are always readable as a numbered list — that is the answer, and it stands on its
 * own. When they also carry the `path` the in-app runner needs, the reader additionally gets a
 * "show me" action that drives the portal through them (`dispatchWalkthrough`, which the
 * app-wide `DynamicWalkthroughProvider` picks up).
 */
const AssistantStepsView: React.FC<AssistantStepsViewProps> = ({steps}) => {
  const intl = useIntl()

  const handleShowMe = useCallback(() => {
    try {
      void dispatchWalkthrough({result: {walkthrough: steps}}, {}, {confirmed: true})
    } catch (e) {
      calculateErrorMessage(e)
    }
  }, [steps])

  if (steps.length === 0) return null

  return (
    <div className='chatbot-steps'>
      <ol className='chatbot-steps__list'>
        {steps.map((step, index) => {
          const title = String(step.title ?? '').trim()
          const text = String(step.text ?? '').trim()
          return (
            <li key={`${title || text}-${index}`} className='chatbot-steps__item'>
              {title ? <span className='chatbot-steps__item-title'>{title}</span> : null}
              {text && text !== title ? (
                <span className='chatbot-steps__item-text'>{text}</span>
              ) : null}
            </li>
          )
        })}
      </ol>
      {stepsAreNavigable(steps) ? (
        <button type='button' className='chatbot-steps__action' onClick={handleShowMe}>
          <PlayCircle size={14} aria-hidden='true' />
          {intl.formatMessage({id: 'CHATBOT.STEPS.SHOW_ME'})}
        </button>
      ) : null}
    </div>
  )
}

export default AssistantStepsView
